import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3,
  Briefcase,
  CalendarRange,
  CheckCircle2,
  Filter,
  LayoutGrid,
  Loader2,
  PieChart as PieIcon,
  RefreshCw,
  Table2,
  Target,
  TrendingUp,
  Users,
  X,
  XCircle,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { isAdminRole } from "../../lib/roles";
import { Card, PageHeader } from "../ui";
import { DateInput } from "../DateInput";

const basePath = (user) =>
  isAdminRole(user?.role) ? "/admin/comercial" : "/portal/comercial";

const ORIGINS = [
  "Diretoria",
  "Conselho",
  "Administração",
  "Evento",
  "Indicação Externa",
  "Outro",
];

// Static chart props hoisted to avoid new objects on every render.
const FUNNEL_MARGIN = { top: 8, right: 20, bottom: 8, left: 110 };
const MONTHLY_MARGIN = { top: 10, right: 20, bottom: 10, left: 0 };
const SEGMENT_MARGIN = { top: 10, right: 20, bottom: 60, left: 0 };
const BAR_CURSOR = { fill: "#f1f5f9" };
const LEGEND_STYLE = { fontSize: 12 };
const LINE_DOT = { r: 3 };
const H_BAR_RADIUS = [0, 4, 4, 0];
const V_BAR_RADIUS = [4, 4, 0, 0];
const funnelTooltip = (v) => [`${v} lead(s)`, "Total"];
const originLabel = (entry) => `${entry.name} (${entry.count})`;

const PIE_COLORS = [
  "#002776", "#009b3a", "#FEDF00", "#6366f1", "#f97316",
  "#ec4899", "#14b8a6", "#8b5cf6", "#ef4444", "#64748b",
];

const KPI_CARDS = [
  { key: "total_leads", label: "Total de Leads", icon: Users, color: "text-slate-700", bg: "bg-slate-50" },
  { key: "new_this_month", label: "Novos no mês", icon: TrendingUp, color: "text-sky-700", bg: "bg-sky-50" },
  { key: "em_negociacao", label: "Em Negociação", icon: Briefcase, color: "text-amber-700", bg: "bg-amber-50" },
  { key: "propostas_enviadas", label: "Propostas Enviadas", icon: BarChart3, color: "text-orange-700", bg: "bg-orange-50" },
  { key: "associacoes_aprovadas", label: "Aprovadas", icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50" },
  { key: "convertidos", label: "Convertidos", icon: Target, color: "text-white", bg: "bg-[#002776]" },
  { key: "perdidos", label: "Perdidos", icon: XCircle, color: "text-rose-700", bg: "bg-rose-50" },
  { key: "taxa_conversao", label: "Taxa de Conversão", icon: PieIcon, color: "text-emerald-700", bg: "bg-emerald-50", suffix: "%" },
];

const monthShort = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[parseInt(m, 10) - 1] || m}/${y.slice(2)}`;
};

export const ComercialDashboard = () => {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [segments, setSegments] = useState([]);
  const [respUsers, setRespUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    stage: "",
    segmento: "",
    origem: "",
    assigned_to: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [s, seg, r] = await Promise.all([
          api.get("/comercial/stages"),
          api.get("/comercial/segments"),
          api.get("/comercial/users/responsaveis"),
        ]);
        setStages(s.data.items || []);
        setSegments(seg.data.items || []);
        setRespUsers(r.data.items || []);
      } catch (err) {
        // Non-blocking: filtros auxiliares ficam vazios se a request falhar
        console.warn("[ComercialDashboard] falha ao carregar filtros:", err);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await api.get("/comercial/dashboard", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () =>
    setFilters({
      date_from: "",
      date_to: "",
      stage: "",
      segmento: "",
      origem: "",
      assigned_to: "",
    });

  const activeFilters = Object.values(filters).filter(Boolean).length;

  const stageLabelMap = useMemo(() => {
    const m = {};
    stages.forEach((s) => (m[s.key] = s.label));
    return m;
  }, [stages]);

  const monthlyData = useMemo(
    () => (data?.charts?.monthly || []).map((m) => ({ ...m, label: monthShort(m.month) })),
    [data]
  );

  return (
    <div data-testid="comercial-dashboard" className="p-6 lg:p-10">
      <PageHeader
        title="Dashboard Comercial"
        subtitle="Visão consolidada do funil, origem e conversão dos leads."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded border border-slate-300 bg-white overflow-hidden">
              <span className="px-3 py-2 text-sm bg-[#002776] text-white inline-flex items-center gap-2">
                <BarChart3 size={14} /> Dashboard
              </span>
              <Link
                to={`${basePath(user)}/kanban`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <LayoutGrid size={14} /> Kanban
              </Link>
              <Link
                to={`${basePath(user)}/leads`}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <Table2 size={14} /> Tabela
              </Link>
            </div>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm rounded hover:bg-slate-50"
              data-testid="btn-toggle-filters"
            >
              <Filter size={14} /> Filtros{activeFilters > 0 && (
                <span className="ml-1 bg-[#002776] text-white text-[11px] px-1.5 rounded-full">
                  {activeFilters}
                </span>
              )}
            </button>
            <button
              onClick={load}
              disabled={loading}
              data-testid="btn-refresh"
              className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm rounded hover:bg-slate-50 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Atualizar
            </button>
          </div>
        }
      />

      {showFilters && (
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Período de
              </span>
              <DateInput
                value={filters.date_from}
                onChange={(v) => setFilters({ ...filters, date_from: v })}
                testid="filter-date-from"
                className="min-w-[150px]"
                compact
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Até
              </span>
              <DateInput
                value={filters.date_to}
                onChange={(v) => setFilters({ ...filters, date_to: v })}
                testid="filter-date-to"
                className="min-w-[150px]"
                compact
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Estágio
              </span>
              <select
                value={filters.stage}
                onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                data-testid="filter-stage"
              >
                <option value="">Todos</option>
                {stages.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Segmento
              </span>
              <select
                value={filters.segmento}
                onChange={(e) => setFilters({ ...filters, segmento: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                data-testid="filter-segmento"
              >
                <option value="">Todos</option>
                {segments.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Origem
              </span>
              <select
                value={filters.origem}
                onChange={(e) => setFilters({ ...filters, origem: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                data-testid="filter-origem"
              >
                <option value="">Todas</option>
                {ORIGINS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                Responsável
              </span>
              <select
                value={filters.assigned_to}
                onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                data-testid="filter-assigned"
              >
                <option value="">Todos</option>
                {respUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {activeFilters > 0 && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="btn-clear-filters"
              >
                <X size={12} /> Limpar filtros
              </button>
            </div>
          )}
        </Card>
      )}

      {loading && !data ? (
        <div className="py-14 text-center text-slate-500">
          <Loader2 className="inline animate-spin mr-2" size={16} /> Carregando dashboard...
        </div>
      ) : !data ? null : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {KPI_CARDS.map((kpi) => {
              const Icon = kpi.icon;
              const val = data.kpis[kpi.key] ?? 0;
              return (
                <Card
                  key={kpi.key}
                  testid={`kpi-${kpi.key}`}
                  className={`p-4 ${kpi.bg} border-0`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className={`text-[11px] uppercase tracking-wider font-semibold ${kpi.color} opacity-80`}>
                        {kpi.label}
                      </div>
                      <div className={`mt-2 font-serif-display text-3xl ${kpi.color}`}>
                        {val}
                        {kpi.suffix || ""}
                      </div>
                    </div>
                    <Icon size={20} className={kpi.color} />
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* 1) Funil */}
            <Card className="p-5" testid="chart-funnel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Funil Comercial</h3>
                <span className="text-xs text-slate-500">Leads por estágio</span>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={data.charts.funnel}
                  layout="vertical"
                  margin={FUNNEL_MARGIN}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} stroke="#64748b" fontSize={11} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    stroke="#64748b"
                    fontSize={11}
                    width={110}
                  />
                  <Tooltip
                    formatter={funnelTooltip}
                    cursor={BAR_CURSOR}
                  />
                  <Bar dataKey="count" radius={H_BAR_RADIUS}>
                    {data.charts.funnel.map((row, idx) => (
                      <Cell key={row.stage || `funnel-${idx}`} fill={row.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* 2) Evolução Mensal */}
            <Card className="p-5" testid="chart-monthly">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Evolução Mensal</h3>
                <span className="text-xs text-slate-500">Novos vs. Convertidos</span>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart
                  data={monthlyData}
                  margin={MONTHLY_MARGIN}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
                  <YAxis allowDecimals={false} stroke="#64748b" fontSize={11} />
                  <Tooltip />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Line
                    type="monotone"
                    dataKey="created"
                    name="Novos leads"
                    stroke="#002776"
                    strokeWidth={2}
                    dot={LINE_DOT}
                  />
                  <Line
                    type="monotone"
                    dataKey="converted"
                    name="Convertidos"
                    stroke="#009b3a"
                    strokeWidth={2}
                    dot={LINE_DOT}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            {/* 3) Origem */}
            <Card className="p-5" testid="chart-origin">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Origem dos Leads</h3>
                <span className="text-xs text-slate-500">
                  {data.charts.origin.length} origens
                </span>
              </div>
              {data.charts.origin.length === 0 ? (
                <div className="py-24 text-center text-slate-400 text-sm">
                  Sem dados no período selecionado
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={data.charts.origin}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      innerRadius={60}
                      paddingAngle={2}
                      label={originLabel}
                      labelLine={false}
                    >
                      {data.charts.origin.map((row, idx) => (
                        <Cell
                          key={row.name || `origin-${idx}`}
                          fill={PIE_COLORS[idx % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* 4) Segmento */}
            <Card className="p-5" testid="chart-segment">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Leads por Segmento</h3>
                <span className="text-xs text-slate-500">
                  Top {data.charts.segment.length}
                </span>
              </div>
              {data.charts.segment.length === 0 ? (
                <div className="py-24 text-center text-slate-400 text-sm">
                  Sem dados no período selecionado
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart
                    data={data.charts.segment}
                    margin={SEGMENT_MARGIN}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="name"
                      stroke="#64748b"
                      fontSize={11}
                      angle={-30}
                      textAnchor="end"
                      height={70}
                      interval={0}
                    />
                    <YAxis allowDecimals={false} stroke="#64748b" fontSize={11} />
                    <Tooltip cursor={BAR_CURSOR} />
                    <Bar dataKey="count" fill="#002776" radius={V_BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {data.generated_at && (
            <div className="text-xs text-slate-500 text-right">
              Dados gerados em{" "}
              {new Date(data.generated_at).toLocaleString("pt-BR")}
            </div>
          )}
        </>
      )}
    </div>
  );
};
